import fields from '@/constants/fields';
import { findWithUUID } from '@/utils/uuidHelper';
import {
  findMatch, hasKey, newName,
} from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import { fetchClauseCountsState } from '../../profile/state/shared';
import cohortProfile from '../../profile/state/state';
import { clauseNames, newCohortClause } from '../../clauses/state/state';
import {
  uniqueDictionariesInClauses,
  findQueryClause,
  assignEditCohortProps,
} from '../../state/shared';
import {
  newCohortFilter,
  newCohortFilterField,
  defaultFilterValues,
  newCohortFilterJoin,
  pruneCohortFilterErrors,
} from './state';
import { pruneCohortQueryErrors } from '../../queries/state/state';
import actions, { cohortFilters, cohortJoinOperators } from '../constants';

const removeFilterFromCohort = withDraft((draft, action) => {
  const { cohort, query, clause } = findQueryClause(draft.cohorts, action);

  assignEditCohortProps(cohort, true);
  cohort.saveErrors = pruneCohortFilterErrors(cohort.saveErrors, action);
  clause.filters = clause.filters.filter((r) => (r.uuid !== action.filterUUID));

  if (clause.filters.length === 1) {
    query.clauses = query.clauses.filter((r) => (r.uuid !== action.clauseUUID));
  }
  fetchClauseCountsState(draft, action);
});

const updateQueryFilterValues = withDraft((draft, action) => {
  const { cohort, clause } = findQueryClause(draft.cohorts, action);
  assignEditCohortProps(cohort, true);
  cohort.saveErrors = pruneCohortFilterErrors(cohort.saveErrors, action, fields.VALUE);

  const filter = findWithUUID(clause.filters, action.filterUUID);
  filter.values = action.values;
});

const updateQueryFilterOperator = withDraft((draft, action) => {
  const { cohort, clause } = findQueryClause(draft.cohorts, action);
  assignEditCohortProps(cohort, true);
  cohort.saveErrors = pruneCohortFilterErrors(cohort.saveErrors, action, fields.OPERATOR);

  const filter = findWithUUID(clause.filters, action.filterUUID);
  filter.operator = action.operator;
  filter.values = defaultFilterValues(action.operator, filter.field.type, filter.values);

  if (!Object.values(cohortJoinOperators).includes(action.operator)) {
    delete filter.join;
  } else if (!hasKey(filter, 'join')) {
    filter.join = newCohortFilterJoin({ name: '', type: '' }, { code: '' });
  }
});

const updateQueryFilterField = withDraft((draft, action) => {
  const { cohort, query, clause } = findQueryClause(draft.cohorts, action);
  assignEditCohortProps(cohort, true);

  const filter = findWithUUID(clause.filters, action.filterUUID);
  const dictionary = query.dataset.dictionaries.find((d) => d.code === clause.dictionary.code);
  const field = dictionary.fields.find((f) => f.name === action.name);

  filter.field = newCohortFilterField(field);
  filter.values = defaultFilterValues(filter.operator, field.type, filter.values);
});

const updateQueryFilterJoin = withDraft((draft, action) => {
  const { cohort, query, clause } = findQueryClause(draft.cohorts, action);
  assignEditCohortProps(cohort, true);
  cohort.saveErrors = pruneCohortFilterErrors(cohort.saveErrors, action, fields.JOIN);

  const filter = findWithUUID(clause.filters, action.filterUUID);
  const join = uniqueDictionariesInClauses(query).find((d) => d.code === action.dictionaryCode);
  const dictionary = query.dataset.dictionaries.find((d) => d.code === action.dictionaryCode);
  const field = dictionary.fields.find((f) => f.name === action.name);
  filter.join = newCohortFilterJoin(field, dictionary);
  filter.values = [];

  if (!join) {
    const name = newName('Clause', clauseNames(query));
    query.clauses.push(newCohortClause(dictionary, name));
    if (!findMatch(query.profiles, dictionary.code)) {
      query.profiles.push(cohortProfile(dictionary.code));
    }
  }
});

const addQueryFilter = withDraft((draft, action) => {
  const { cohort, query, clause } = findQueryClause(draft.cohorts, action);
  assignEditCohortProps(cohort, true);
  cohort.saveErrors = pruneCohortQueryErrors(cohort.saveErrors, action, fields.CLAUSE);
  const filter = clause.filters.find((f) => f.isNew);
  const dictionary = query.dataset.dictionaries.find((d) => d.code === clause.dictionary.code);
  const field = dictionary.fields.find((f) => f.name === action.name);
  const operators = cohortFilters[field.type];
  const operator = operators[Object.keys(operators)[0]];

  filter.field = newCohortFilterField(field);
  filter.operator = operator;
  filter.values = defaultFilterValues(operator, field.type);
  delete filter.isNew;
  clause.filters.push(newCohortFilter());

  if (clause.isNew) {
    delete clause.isNew;
    const name = newName('Clause', clauseNames(query));
    query.clauses.push(newCohortClause(clause.dictionary, name));
  }
  fetchClauseCountsState(draft, action);
});

export default {
  [actions.ADD_QUERY_FILTER]: addQueryFilter,
  [actions.REMOVE_FILTER_FROM_COHORT]: removeFilterFromCohort,
  [actions.UPDATE_QUERY_FILTER_FIELD]: updateQueryFilterField,
  [actions.UPDATE_QUERY_FILTER_JOIN]: updateQueryFilterJoin,
  [actions.UPDATE_QUERY_FILTER_OPERATOR]: updateQueryFilterOperator,
  [actions.UPDATE_QUERY_FILTER_VALUES]: updateQueryFilterValues,
};
